import { getUtm } from './utm';

// Domain configuration for multi-domain setup
// prepframe.com = marketing site (landing, pricing, demo, SEO pages)
// app.prepframe.com = authenticated app (dashboard, shoots, settings)

export const APP_DOMAIN = 'app.prepframe.com';
export const LANDING_DOMAIN = 'prepframe.com';
export const CANONICAL_MARKETING_DOMAIN = 'www.prepframe.com';

// Marketing pages - served on prepframe.com (indexable)
export const MARKETING_ROUTES = [
  '/',
  '/pricing',
  '/demo',
  '/privacy',
  '/terms',
  '/contact',
  '/unsubscribe',
  '/client-brief',
  '/field-view',
  '/projects',
  '/wedding-photography-brief',
  '/portrait-photography-brief',
  '/newborn-photography-brief',
  '/commercial-photography-brief',
  '/real-estate-photography-brief',
  '/event-photography-brief',
  '/blog',
  '/blog/photography-client-questionnaire',
  '/blog/wedding-photography-client-intake',
  '/blog/photography-questionnaire-template-free',
  '/blog/photography-shot-list',
  '/blog/client-photography-questionnaire-questions',
];

// App routes - served on app.prepframe.com (noindex)
export const APP_ROUTES = [
  '/auth',
  '/reset-password',
  '/dashboard',
  '/shoots',
  '/settings',
  '/admin/',
  '/b/', // client briefs
];

/**
 * Check if a path is a marketing route
 */
export function isMarketingRoute(pathname: string): boolean {
  // Exact match for most routes
  if (MARKETING_ROUTES.includes(pathname)) return true;
  // Root path
  if (pathname === '/') return true;
  // All blog posts (current + future)
  if (pathname.startsWith('/blog')) return true;
  return false;
}

/**
 * Check if a path is an app route
 */
export function isAppRoute(pathname: string): boolean {
  return APP_ROUTES.some(route => pathname.startsWith(route));
}

/**
 * Check if we're on the app domain (app.prepframe.com)
 * Note: For redirect purposes, only returns true for actual app.prepframe.com
 * localhost and lovable.app are handled separately to avoid redirect loops
 */
export function isAppDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === APP_DOMAIN;
}

/**
 * Check if we're on the landing domain (prepframe.com)
 */
export function isLandingDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === LANDING_DOMAIN || host === `www.${LANDING_DOMAIN}`;
}

const UTM_PARAM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/**
 * Get the app URL (always app.prepframe.com in production).
 * Relays any UTM params currently in the URL so attribution survives the
 * cross-subdomain navigation from www.prepframe.com → app.prepframe.com.
 */
export function getAppUrl(path: string = ''): string {
  if (typeof window === 'undefined') return `https://${APP_DOMAIN}${path}`;

  const host = window.location.hostname;
  const base = (host === 'localhost' || host.includes('.vercel.app'))
    ? `${window.location.origin}${path}`
    : `https://${APP_DOMAIN}${path}`;

  const url = new URL(base);
  const currentParams = new URLSearchParams(window.location.search);
  const storedUtm = getUtm();
  for (const key of UTM_PARAM_KEYS) {
    const val = currentParams.get(key) || storedUtm[key];
    if (val) url.searchParams.set(key, val);
  }
  return url.toString();
}

/**
 * Get the marketing URL (always prepframe.com in production)
 */
export function getMarketingUrl(path: string = ''): string {
  if (typeof window === 'undefined') return `https://${CANONICAL_MARKETING_DOMAIN}${path}`;
  
  const host = window.location.hostname;
  
  // In development or Vercel preview, use current origin
  if (host === 'localhost' || host.includes('.vercel.app')) {
    return `${window.location.origin}${path}`;
  }
  
  // In production, always use www.prepframe.com (canonical) for marketing routes
  return `https://${CANONICAL_MARKETING_DOMAIN}${path}`;
}
